/**
 * Phase 1.7 — ViewToggle (grid / list).
 *
 * Module pages may render a card grid or a dense list. The choice is a
 * per-module UI preference, persisted in `ui_prefs.view_modes[<key>]`.
 * The toggle itself is dumb — caller wires it to useUiPrefs().
 */

import { LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ViewMode = 'grid' | 'list';

export interface ViewToggleProps {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
  className?: string;
}

export function ViewToggle({ value, onChange, className }: ViewToggleProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label="View mode"
      className={cn(
        'inline-flex items-center rounded-md border border-border bg-surface p-0.5',
        'dark:bg-surface-dark dark:border-border',
        className,
      )}
    >
      <Btn icon={<LayoutGrid className="h-4 w-4" />} active={value === 'grid'} onClick={() => onChange('grid')} label="Grid" />
      <Btn icon={<List className="h-4 w-4" />} active={value === 'list'} onClick={() => onChange('list')} label="List" />
    </div>
  );
}

function Btn({
  icon,
  label,
  active,
  onClick,
}: {
  icon: JSX.Element;
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded transition-colors',
        active
          ? 'bg-burnt-orange text-white shadow-sm dark:bg-burnt-orange-dark'
          : 'text-muted-foreground hover:bg-surface-raised hover:text-charcoal dark:hover:bg-surface-raised-dark dark:hover:text-charcoal-dark',
      )}
    >
      {icon}
    </button>
  );
}
